import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StoreIcon from '@mui/icons-material/Store';

import { buildPath } from '@e-socna/utils';
import { formatSiret } from '@e-socna/third-parties';
import type { Id } from '@e-socna/data-layer';

import { PATH_ORGANIZATION_GENERAL } from '../../app/constants/paths.constant';

import { CreationStatusIndicator } from './CreationStatusIndicator';
import { useEstablishmentOrganizationCreation } from './hooks/useEstablishmentOrganizationCreation.hook';
import type { EstablishmentSettledOutcome } from './hooks/useEstablishmentOrganizationCreation.hook';
import type { ActiveMembership } from './types/active-membership.type';
import type { EstablishmentListItem } from './utils/build-establishments-list.util';
import type { OrganizationSearchResult } from './types/organization-search-result.type';

interface Props {
  readonly activeMemberships: readonly ActiveMembership[];
  readonly canStart: boolean;
  readonly index: number;
  readonly isSelected: boolean;
  readonly isWaitingForHeadOffice: boolean;
  readonly item: EstablishmentListItem;
  readonly motherId: Id | null;
  readonly onSettled: (
    index: number,
    outcome: Readonly<EstablishmentSettledOutcome>,
  ) => void;
  readonly result: OrganizationSearchResult;
}

export const EstablishmentResultRow = ({
  activeMemberships,
  canStart,
  index,
  isSelected,
  isWaitingForHeadOffice,
  item,
  motherId,
  onSettled,
  result,
}: Readonly<Props>) => {
  const handleSettled = useCallback(
    (outcome: Readonly<EstablishmentSettledOutcome>) => {
      onSettled(index, outcome);
    },
    [index, onSettled],
  );

  const { establishment, isHeadOffice } = item;

  const { errorMessage, membershipsApplied, organizationId, retry, status } =
    useEstablishmentOrganizationCreation({
      activeMemberships,
      canStart,
      establishment,
      isHeadOffice,
      motherId,
      onSettled: handleSettled,
      result,
    });
  const isMembershipsWarning =
    status === 'success' && membershipsApplied === false;

  return (
    <Stack spacing={0.5} sx={{ opacity: isSelected ? 1 : 0.5 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
          {isHeadOffice ? (
            <BusinessIcon color="action" fontSize="small" />
          ) : (
            <StoreIcon color="action" fontSize="small" />
          )}
          <Typography variant="body2">
            {isHeadOffice ? 'Siège' : 'Établissement'} -{' '}
            {formatSiret(establishment.siret)}
          </Typography>
        </Stack>
        {isSelected && isWaitingForHeadOffice ? (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', color: 'text.secondary', gap: 0.5 }}
          >
            <HourglassEmptyIcon fontSize="small" />
            <Typography variant="caption">En attente du siège</Typography>
          </Stack>
        ) : null}
        {isSelected && !isWaitingForHeadOffice ? (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', flexShrink: 0, gap: 1 }}
          >
            <CreationStatusIndicator
              infoMessage={
                status === 'error'
                  ? errorMessage
                  : isMembershipsWarning
                    ? "Organisation créée, mais les membres n'ont pas pu être appliqués."
                    : undefined
              }
              isInfoAWarning={status === 'success'}
              onRetry={
                status === 'error' || isMembershipsWarning ? retry : undefined
              }
              status={status}
            />
            {status === 'success' && organizationId ? (
              <>
                <Button
                  component={Link}
                  size="small"
                  to={buildPath(PATH_ORGANIZATION_GENERAL, {
                    id: organizationId,
                  })}
                >
                  Consulter
                </Button>
                <Button
                  component={MuiLink}
                  endIcon={<OpenInNewIcon />}
                  href={buildPath(PATH_ORGANIZATION_GENERAL, {
                    id: organizationId,
                  })}
                  rel="noopener noreferrer"
                  size="small"
                  target="_blank"
                >
                  Nouvel onglet
                </Button>
              </>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      {establishment.adresse ? (
        <Typography color="text.secondary" sx={{ pl: 3.5 }} variant="body2">
          {establishment.adresse}
        </Typography>
      ) : null}
    </Stack>
  );
};
